<template>
  <div v-if="hidden" />

  <component :is="inputComponent" v-else
             v-bind="inputComponentProps" :show-label="showLabel"
             v-on="inputComponentListener" @focus="$emit('focus')" />
</template>

<script>
import CheckboxInput from './checkbox-input'
import RadioButtonsInput from './radio-buttons-input'
import SelectorInput from './selector-input'
import TextInput from './text-input'
import WithInputIdAndName from '@/components/inputs/mixins/with-input-id-and-name'

export default {
  components: { CheckboxInput, RadioButtonsInput, SelectorInput, TextInput },

  mixins: [ WithInputIdAndName ],

  props: {
    disclaimer: {
      type: String,
      required: false
    },
    entityName: {
      type: String,
      required: true
    },
    initialValue: {
      // We must put String before Boolean here, otherwise passing in an empty string
      // can cause the value to become "true"
      type: [String, Boolean],
      required: false,
      default: null
    },
    kind: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    maxLength: {
      type: Number,
      required: false
    },

    minLength: {
      type: Number,
      required: false
    },
    fieldName: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      required: false
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    showLabel: {
      type: Boolean,
      required: false,
      default: true
    },
    validationErrors: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  emits: ['changed', 'focus'],

  computed: {
    hidden() {
      return this.kind === 'hidden'
    },

    inputComponent() {
      switch (this.kind) {
      case 'boolean':
      case 'conditional_boolean':
        return CheckboxInput
      case 'radio_buttons':
        return RadioButtonsInput
      case 'select':
        return SelectorInput
      case 'string':
        return TextInput
      default:
        throw `unsupported type ${this.kind}`
      }
    },

    inputComponentListener() {
      switch (this.kind) {
      case 'boolean':
      case 'conditional_boolean':
      case 'radio_buttons':
        return {
          change: this.update
        }
      case 'select':
      case 'string':
        return {
          changed: this.update
        }
      default:
        throw `unsupported type ${this.kind}`
      }
    },

    inputComponentProps() {
      const commonProps = {
        label: this.label,
        required: this.required,
        validationErrors: this.validationErrors
      }

      switch (this.kind) {
      case 'boolean':
      case 'conditional_boolean':
        return {
          ...commonProps,
          checkboxId: this.calculatedInputId,
          label: this.labelWithDisclaimer,
          inputName: this.calculatedInputName,
          initialValue: this.initialValue
        }
      case 'radio_buttons':
        return {
          ...commonProps,
          helpText: this.disclaimer,
          inputName: this.calculatedInputName,
          options: this.options,
          initialValue: this.initialValue
        }
      case 'select':
        return {
          ...commonProps,
          field: this.calculatedInputName,
          helpText: this.disclaimer,
          id: this.calculatedInputId,
          options: this.options,
          initialValue: this.initialValue
        }
      case 'string':
        return {
          ...commonProps,
          helpText: this.disclaimer,
          inputId: this.calculatedInputId,
          inputName: this.calculatedInputName,
          minLength: this.minLength,
          maxLength: this.maxLength,
          value: this.initialValue
        }
      default:
        throw `unsupported type ${this.kind}`
      }
    },

    // We override this method to avoid changing the field's name casing
    calculatedInputName() {
      if (this.inputName) {
        return this.inputName
      }

      return `${this.entityName}[${this.fieldName}]`
    },

    labelWithDisclaimer() {
      if (this.disclaimer) {
        return `${this.label}<div class="text-muted">${this.disclaimer}</div>`
      } else {
        return this.label
      }
    }
  },

  mounted() {
    // Need to emit to record the initial value of the field, which is especially
    // important for checkboxes, otherwise the "false" value is not recorded
    // and it's not submitted to the server.
    this.update(this.initialValue)
  },

  methods: {
    update(value) {
      this.$emit('changed', value)
    }
  }
}
</script>
